import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, 
  Bookmark, 
  MessageSquare, 
  Send, 
  LogOut, 
  Home,
  Trophy,
  UserCircle,
  Calendar,
  Eye,
  ChevronDown,
  ChevronRight,
  Library as LibraryIcon,
  Shield,
  Building2,
  UserPlus,
  Wrench,
  Lightbulb,
  MessageCircle,
  Bell
} from "lucide-react";
import DashboardHeader from "@/components/dashboard/DashboardHeader";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useQuery } from "@tanstack/react-query";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";

// Cross icon component for logo - matches Layout.tsx
const CrossIcon = () => (
  <svg viewBox="0 0 10 16" width="8" height="14" className="inline-block align-baseline" style={{ verticalAlign: 'baseline', marginBottom: '-1px' }}>
    <rect x="3" y="0" width="4" height="16" fill="currentColor" rx="1"/>
    <rect x="0" y="4" width="10" height="3" fill="currentColor" rx="1"/>
  </svg>
);

const DashboardLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { organization, isSuperAdmin } = useOrganization();
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();

  // Collapsible state - always starts collapsed
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [challengesOpen, setChallengesOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Check if user has admin/editor/moderator role
  useEffect(() => {
    const checkUserRoles = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: roles } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id);
        
        const roleList = roles?.map(r => r.role) ?? [];
        setHasAdminAccess(
          roleList.includes('admin') || 
          roleList.includes('editor') || 
          roleList.includes('moderator')
        );
      }
    };
    checkUserRoles();
  }, []);

  // Get counts for badges
  const { data: counts } = useQuery({
    queryKey: ["dashboard-nav-counts"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { events: 0, challenges: 0, eventsFollowing: 0, challengesFollowing: 0 };

      const [events, challenges, eventsFollowing, challengesFollowing] = await Promise.all([
        supabase.from("event_registrations").select("id", { count: "exact", head: true }).eq("user_id", user.id),
        supabase.from("challenge_team_members").select("id", { count: "exact", head: true }).eq("user_id", user.id),
        supabase.from("user_follows").select("id", { count: "exact", head: true }).eq("user_id", user.id).eq("entity_type", "event"),
        supabase.from("user_follows").select("id", { count: "exact", head: true }).eq("user_id", user.id).eq("entity_type", "challenge"),
      ]);

      return {
        events: events.count ?? 0,
        challenges: challenges.count ?? 0,
        eventsFollowing: eventsFollowing.count ?? 0,
        challengesFollowing: challengesFollowing.count ?? 0,
      };
    },
    staleTime: 30000,
  });

  const isActive = (path: string) =>
    location.pathname === path || 
    (path !== "/dashboard" && location.pathname.startsWith(path));

  const linkClass = (path: string, indent = false) =>
    cn(
      "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
      indent && "ml-4",
      isActive(path) 
        ? "bg-primary/10 text-primary" 
        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
    );

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error("Failed to log out");
    } else {
      toast.success("Logged out successfully");
      navigate("/login");
    }
  };

  const SectionHeader = ({ 
    icon: Icon, 
    label, 
    isOpen, 
    onToggle 
  }: { 
    icon: React.ElementType; 
    label: string; 
    isOpen: boolean;
    onToggle: () => void;
  }) => (
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:bg-accent/50 rounded-md transition-colors"
    >
      {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
      <Icon className="h-3.5 w-3.5" />
      <span>{label}</span>
    </button>
  );

  // Extracted sidebar content for reuse in desktop and mobile
  const SidebarContent = () => (
    <>
      <div className="h-14 flex items-center px-4 border-b border-border">
        <Link to="/" className="text-lg font-bold tracking-tight font-display hover:opacity-80 transition-opacity whitespace-nowrap flex items-baseline">
          {organization?.slug === 'spes' ? (
            <span className="text-primary font-bold">SPES</span>
          ) : (
            <>BuildWithChrist<span className="text-primary">.a<CrossIcon /></span></>
          )}
        </Link>
      </div>
      <nav className="flex-grow p-4 space-y-1 text-sm font-medium overflow-y-auto">
        {/* Overview - always visible */}
        <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          My Dashboard
        </div>
        <Link to="/dashboard" className={linkClass("/dashboard")}>
          <LayoutDashboard size={18} /> Overview
        </Link>

        {/* Events Section - Collapsible (Hidden for SPES) */}
        {organization?.slug !== 'spes' && (
          <Collapsible open={eventsOpen} onOpenChange={setEventsOpen} className="mt-4">
            <CollapsibleTrigger asChild>
              <SectionHeader 
                icon={Calendar} 
                label="Events" 
                isOpen={eventsOpen} 
                onToggle={() => setEventsOpen(!eventsOpen)} 
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-1 mt-1">
              <Link to="/dashboard/events" className={linkClass("/dashboard/events", true)}>
                <Calendar size={18} /> 
                <span className="flex-1">My Events</span>
                {(counts?.events ?? 0) > 0 && (
                  <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                    {counts?.events}
                  </span>
                )}
              </Link>
              <Link to="/dashboard/following?tab=events" className={linkClass("/dashboard/following", true)}>
                <Eye size={18} /> 
                <span className="flex-1">Following</span>
                {(counts?.eventsFollowing ?? 0) > 0 && (
                  <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                    {counts?.eventsFollowing}
                  </span>
                )}
              </Link>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Challenges Section - Collapsible (Hidden for SPES) */}
        {organization?.slug !== 'spes' && (
          <Collapsible open={challengesOpen} onOpenChange={setChallengesOpen} className="mt-4">
            <CollapsibleTrigger asChild>
              <SectionHeader 
                icon={Trophy} 
                label="Challenges" 
                isOpen={challengesOpen} 
                onToggle={() => setChallengesOpen(!challengesOpen)} 
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-1 mt-1">
              <Link to="/dashboard/challenges" className={linkClass("/dashboard/challenges", true)}>
                <Trophy size={18} /> 
                <span className="flex-1">My Challenges</span>
                {(counts?.challenges ?? 0) > 0 && (
                  <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                    {counts?.challenges}
                  </span>
                )}
              </Link>
              <Link to="/dashboard/following?tab=challenges" className={linkClass("/dashboard/following", true)}>
                <Eye size={18} /> 
                <span className="flex-1">Following</span>
                {(counts?.challengesFollowing ?? 0) > 0 && (
                  <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                    {counts?.challengesFollowing}
                  </span>
                )}
              </Link>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Tools Section - SPES Only */}
        {organization?.slug === 'spes' && (
          <Collapsible open={toolsOpen} onOpenChange={setToolsOpen} className="mt-4">
            <CollapsibleTrigger asChild>
              <SectionHeader 
                icon={Wrench} 
                label="Tools" 
                isOpen={toolsOpen} 
                onToggle={() => setToolsOpen(!toolsOpen)} 
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-1 mt-1">
              <Link to="/sandbox" className={linkClass("/sandbox", true)}>
                <Lightbulb size={18} /> Leadership Plan
              </Link>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Chat Section - SPES Only */}
        {organization?.slug === 'spes' && (
          <Collapsible open={chatOpen} onOpenChange={setChatOpen} className="mt-4">
            <CollapsibleTrigger asChild>
              <SectionHeader 
                icon={MessageCircle} 
                label="Chat" 
                isOpen={chatOpen} 
                onToggle={() => setChatOpen(!chatOpen)} 
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-1 mt-1">
              <Link to="/dashboard/chat" className={linkClass("/dashboard/chat", true)}>
                <MessageCircle size={18} /> AI Assistant
              </Link>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Library Section - Collapsible */}
        <Collapsible open={libraryOpen} onOpenChange={setLibraryOpen} className="mt-4">
          <CollapsibleTrigger asChild>
            <SectionHeader 
              icon={LibraryIcon} 
              label={organization?.slug === 'spes' ? 'Repository' : 'Library'} 
              isOpen={libraryOpen} 
              onToggle={() => setLibraryOpen(!libraryOpen)} 
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-1 mt-1">
            <Link to="/dashboard/collection" className={linkClass("/dashboard/collection", true)}>
              <Bookmark size={18} /> My Collection
            </Link>
            <Link to="/dashboard/submissions" className={linkClass("/dashboard/submissions", true)}>
              <Send size={18} /> My Submissions
            </Link>
          </CollapsibleContent>
        </Collapsible>

        {/* Account Section */}
        <div className="mt-4 px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Account
        </div>
        <Link to="/dashboard/chat-history" className={linkClass("/dashboard/chat-history")}>
          <MessageSquare size={18} /> Chat History
        </Link>
        <Link to="/dashboard/invites" className={linkClass("/dashboard/invites")}>
          <UserPlus size={18} /> My Invites
        </Link>
        <Link to="/dashboard/notifications" className={linkClass("/dashboard/notifications")}>
          <Bell size={18} /> Notifications
        </Link>
        <Link to="/dashboard/profile" className={linkClass("/dashboard/profile")}>
          <UserCircle size={18} /> My Profile
        </Link>
      </nav>
      <div className="p-4 border-t border-border space-y-1">
        {isSuperAdmin && (
          <Link 
            to="/super-admin" 
            className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <Shield size={18} /> Super Admin
          </Link>
        )}
        {hasAdminAccess && (
          <Link 
            to="/admin" 
            className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <Building2 size={18} /> Org Admin
          </Link>
        )}
        <Link to="/" className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground">
          <Home size={18} /> Back to Site
        </Link>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
        >
          <LogOut size={18} /> Log Out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop Sidebar - hidden on mobile/tablet */}
      <div className="hidden lg:flex w-64 bg-card border-r border-border flex-col">
        <SidebarContent />
      </div>

      {/* Mobile Sheet Sidebar */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="w-72 p-0 bg-card flex flex-col">
          <SidebarContent />
        </SheetContent>
      </Sheet>

      {/* Main content area - full width on mobile */}
      <div className="flex-grow flex flex-col overflow-hidden">
        <DashboardHeader 
          showMenuButton={true}
          onMenuClick={() => setMobileMenuOpen(true)}
        />
        <div className="flex-grow overflow-auto">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
